'use client';

import React, { useState, useEffect } from 'react';
import { useAdManagement } from '@/hooks/useAdManagement';
import { ExternalLink } from 'lucide-react';

interface ArticleAdSpotProps {
    location?: string;
    className?: string;
}

// Sidebar locations need a vertical card format
const SIDEBAR_LOCATIONS = [
    'article-sidebar',
    'home-sidebar',
    'tag-page-sidebar',
    'market-hub-sidebar',
    'global-sidebar'
];

export default function ArticleAdSpot({
    location = 'article-footer',
    className = ''
}: ArticleAdSpotProps) {
    const { getActiveAdsByLocation, trackImpression, trackClick, loading } = useAdManagement();
    const [isVisible, setIsVisible] = useState(true);

    // Get the highest priority active ad for this location
    // For sidebar locations, fall back to global-sidebar if no page-specific ad exists
    const activeAd = React.useMemo(() => {
        const ads = getActiveAdsByLocation(location);
        if (ads.length > 0) return ads[0];
        
        // Fallback to global-sidebar for sidebar locations
        if (SIDEBAR_LOCATIONS.includes(location) && location !== 'global-sidebar') {
            const fallbackAds = getActiveAdsByLocation('global-sidebar');
            return fallbackAds.length > 0 ? fallbackAds[0] : null;
        }
        
        return null;
    }, [getActiveAdsByLocation, location]);

    // Track impression when ad becomes visible
    useEffect(() => {
        if (activeAd && isVisible) {
            trackImpression(activeAd.id);
        }
    }, [activeAd, isVisible, trackImpression]);

    const handleClick = async () => {
        if (!activeAd) return;

        // Track click
        await trackClick(activeAd.id);

        // Open URL
        if (activeAd.buttonUrl) {
            if (activeAd.buttonUrl.startsWith('mailto:')) {
                window.location.href = activeAd.buttonUrl;
            } else {
                window.open(activeAd.buttonUrl, '_blank', 'noopener,noreferrer');
            }
        }
    };

    // Don't render while loading or if no active ad
    if (loading || !activeAd || !isVisible) {
        return null;
    }

    // Determine format based on location or ad format
    const isSidebar = SIDEBAR_LOCATIONS.includes(location) || activeAd.format === 'sidebar-rectangle';
    const isFeedBanner = activeAd.format === 'compact-banner';
    const isInlineCard = activeAd.format === 'inline-card' || activeAd.format === 'hero-banner' && (location === 'tag-page-feed' || location === 'home-feed-inline');

    // Inline card variant (horizontal card for feed placement)
    if (isInlineCard) {
        return (
            <div
                className={`relative w-full overflow-hidden rounded-xl transition-all duration-300 cursor-pointer group border border-gray-200 dark:border-neutral-700 hover:border-divider ${className}`}
                onClick={handleClick}
                style={{ backgroundColor: activeAd.backgroundColor }}
            >
                {/* Sponsored Badge */}
                <div className="absolute top-3 right-3 z-10">
                    <span className="bg-black/30 backdrop-blur-sm text-white px-2 py-1 rounded text-[9px] font-medium uppercase tracking-wider">
                        Sponsored
                    </span>
                </div>

                <div className="flex flex-col sm:flex-row">
                    {/* Image */}
                    {activeAd.imageUrl && (
                        <div className="w-full sm:w-1/3 aspect-video sm:aspect-[4/3] relative overflow-hidden bg-gray-100 dark:bg-neutral-800 flex-shrink-0">
                            <img
                                src={activeAd.imageUrl}
                                alt={activeAd.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 p-4 sm:p-5 flex flex-col justify-center">
                        <h3
                            className="text-lg sm:text-xl font-bold mb-2 leading-tight line-clamp-2"
                            style={{ color: activeAd.textColor }}
                        >
                            {activeAd.title}
                        </h3>
                        <p
                            className="text-sm opacity-90 mb-4 line-clamp-2"
                            style={{ color: activeAd.textColor }}
                        >
                            {activeAd.description}
                        </p>

                        {/* CTA Button */}
                        <button
                            className="self-start px-5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 hover:opacity-90 hover:scale-105 active:scale-95 flex items-center gap-2"
                            style={{
                                backgroundColor: activeAd.textColor,
                                color: activeAd.backgroundColor
                            }}
                        >
                            {activeAd.buttonText}
                            <ExternalLink size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Sidebar card variant (vertical layout for sidebars)
    if (isSidebar) {
        return (
            <div
                className={`relative w-full overflow-hidden rounded-xl transition-all duration-300 cursor-pointer group ${className}`}
                onClick={handleClick}
                style={{
                    backgroundColor: activeAd.backgroundColor,
                }}
            >
                {/* Sponsored Badge */}
                <div className="absolute top-2 right-2 z-10">
                    <span className="bg-black/30 backdrop-blur-sm text-white px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider">
                        Sponsored
                    </span>
                </div>

                {/* Image */}
                {activeAd.imageUrl && (
                    <div className="w-full aspect-[4/3] relative overflow-hidden bg-gray-50 dark:bg-brand-dark-lighter/50">
                        <img
                            src={activeAd.imageUrl}
                            alt={activeAd.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                    </div>
                )}

                {/* Content */}
                <div className="p-4">
                    <h3
                        className="text-base font-bold mb-2 leading-tight line-clamp-2"
                        style={{ color: activeAd.textColor }}
                    >
                        {activeAd.title}
                    </h3>
                    <p
                        className="text-sm opacity-90 mb-3 line-clamp-2"
                        style={{ color: activeAd.textColor }}
                    >
                        {activeAd.description}
                    </p>

                    {/* CTA Button */}
                    <button
                        className="w-full px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                        style={{
                            backgroundColor: activeAd.textColor,
                            color: activeAd.backgroundColor
                        }}
                    >
                        {activeAd.buttonText}
                        <ExternalLink size={14} />
                    </button>
                </div>
            </div>
        );
    }

    // Compact feed banner variant
    if (isFeedBanner) {
        return (
            <div
                className={`relative w-full overflow-hidden rounded-lg transition-all duration-300 cursor-pointer group my-4 ${className}`}
                onClick={handleClick}
                style={{
                    backgroundColor: activeAd.backgroundColor,
                    borderColor: `${activeAd.backgroundColor}20`
                }}
            >
                <div className="flex items-center gap-4 p-4">
                    {/* Compact Image */}
                    {activeAd.imageUrl && (
                        <div className="w-20 h-20 flex-shrink-0 relative overflow-hidden bg-gray-50 dark:bg-brand-dark-lighter/50 rounded-lg">
                            <img
                                src={activeAd.imageUrl}
                                alt={activeAd.title}
                                className="w-full h-full object-contain"
                            />
                        </div>
                    )}

                    {/* Compact Content */}
                    <div className="flex-1 min-w-0">
                        <h3
                            className="text-lg font-bold mb-1 leading-tight truncate"
                            style={{ color: activeAd.textColor }}
                        >
                            {activeAd.title}
                        </h3>
                        <p
                            className="text-sm opacity-90 line-clamp-2"
                            style={{ color: activeAd.textColor }}
                        >
                            {activeAd.description}
                        </p>
                    </div>

                    {/* Compact CTA */}
                    <button
                        className="px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 hover:opacity-90 hover:scale-105 active:scale-95 flex items-center gap-2 flex-shrink-0"
                        style={{
                            backgroundColor: activeAd.textColor,
                            color: activeAd.backgroundColor
                        }}
                    >
                        {activeAd.buttonText}
                        <ExternalLink size={14} />
                    </button>

                    {/* Sponsored Badge */}
                    <div className="absolute top-2 right-2 z-10">
                        <span className="bg-black/20 backdrop-blur-sm text-white px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider">
                            Sponsored
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    // Standard large banner variant
    return (
        <div
            className={`relative w-full overflow-hidden rounded-2xl transition-all duration-300 cursor-pointer group my-12 ${className}`}
            onClick={handleClick}
            style={{
                backgroundColor: activeAd.backgroundColor,
                borderColor: `${activeAd.backgroundColor}20`
            }}
        >
            <div className="flex flex-col md:flex-row items-center">
                {/* Ad Image (if available) */}
                {activeAd.imageUrl && (
                    <div className="w-full md:w-1/3 h-48 md:h-auto self-stretch relative overflow-hidden bg-gray-50 dark:bg-brand-dark-lighter/50 flex items-center justify-center p-4">
                        <img
                            src={activeAd.imageUrl}
                            alt={activeAd.title}
                            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                        />
                    </div>
                )}

                {/* Ad Content */}
                <div className={`flex-1 p-6 md:p-8 flex flex-col items-center text-center ${activeAd.imageUrl ? 'md:items-start md:text-left' : 'items-center text-center'}`}>
                    <h3
                        className="text-2xl md:text-3xl font-bold mb-3 leading-tight"
                        style={{ color: activeAd.textColor }}
                    >
                        {activeAd.title}
                    </h3>

                    <p
                        className="text-base md:text-lg mb-6 max-w-2xl opacity-90 leading-relaxed"
                        style={{ color: activeAd.textColor }}
                    >
                        {activeAd.description}
                    </p>

                    {/* CTA Button */}
                    <button
                        className="px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200 hover:opacity-90 hover:scale-105 active:scale-95 flex items-center gap-2"
                        style={{
                            backgroundColor: activeAd.textColor,
                            color: activeAd.backgroundColor
                        }}
                    >
                        {activeAd.buttonText}
                        <ExternalLink size={16} />
                    </button>
                </div>
            </div>

            {/* Sponsored Badge */}
            <div className="absolute top-3 right-3 z-10">
                <span className="bg-black/20 backdrop-blur-sm text-white px-2 py-1 rounded text-[10px] font-medium uppercase tracking-wider">
                    Sponsored
                </span>
            </div>
        </div>
    );
}
